import {selectDefined} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import {
  LabelingStrategy,
  Registry,
  SchemalessBulkImportSupportedOligoAwareTypes,
  SPREADSHEET_LABELING_STRATEGIES,
} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {toSeconds} from 'performance/PerformanceUtils';
import preferences from 'Preferences';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import * as LabelingStrategiesUtil from 'registry/labeling-strategies/LabelingStrategiesUtil';
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import {getTagSchemaOligoAwareFolderItemType} from 'tag-schema/util/TagSchemaUtil';
import pickDefined from 'util/pickDefined';
import {Optional} from 'util/Types';

import {MonomerMapping} from './panes/MonomerMappingForm';
import {
  COLUMN_TYPE,
  ColumnTransform,
  DerivedColumn,
  DerivedColumnsData,
  EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
  ErrorData,
  FileData,
  GetValidColumnTypesFunction,
  IMPORT_TYPE,
  ImportSourceType,
  MapColumnsPane,
  OPTIONS,
  OPTIONS_SAVED_AS_PREFERENCE,
  Pane,
  PaneKey,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
  SpreadsheetBulkImportPreferences,
  SpreadsheetColumn,
  TaskState,
} from './SpreadsheetBulkImportConstants';
import {FindDerivedColumnsResponse} from './SpreadsheetBulkImportDAO';
import {
  amendWithGuessedType,
  getDerivedColumnsWithGuessedTypes,
  getTagSchema,
} from './SpreadsheetBulkImportUtil';

export interface RegistryImportSettings {
  inOrgCollection: boolean;
  inUserCollection: boolean;
  labelingStrategy: RegistryLabelingStrategy | null;
  trialSubjectLabelingStrategy: RegistryLabelingStrategy | null;
}

export interface SelectedDestination {
  folderId: string | null;
  registryId: string | null;
}

export type BulkImportOptions = {[option in OPTIONS]: boolean};

export interface ImportSettings {
  linkFolderIds?: Array<string>;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes | null;
  selectedDestination?: SelectedDestination | null;
  selectedTagSchemaId?: string | null;
  selectedContainerSchemaId?: string | null;
  selectedPlateSchemaId?: string | null;
  selectedBoxSchemaId?: string | null;
  selectedLocationSchemaId?: string | null;
  selectedSampleGroupSchemaId?: string | null;
  selectedResultSchemaId?: string | null;
  selectedProcessTaskSchemaId?: string | null;
  selectedProcessOutputSchemaId?: string | null;
  selectedTrialSubjectSchemaId?: string | null;
  registryImportSettings?: Partial<RegistryImportSettingsProps>;
}

export interface SpreadsheetBulkImportState {
  derivedColumnsData: DerivedColumnsData;
  file: File | null;
  fileData: FileData | null;
  selectedSchemaIds: SelectedSchemaIds;
  importSourceType: ImportSourceType;
  importType: IMPORT_TYPE | null;
  errorData: ErrorData | null;
  linkFolderIds: Array<string>;
  monomerMapping: MonomerMapping | null;
  panes: Array<Pane>;
  panesTimeTracking: {[paneKey in PaneKey]?: number};
  panesTimeTrackingCurrentTimestamp: number;
  paneKey: PaneKey | null;
  rawText: string;
  registryImportSettings: RegistryImportSettingsProps;
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null;
  spreadsheetColumnsMappingName: string | null;
  tableColumnNames: Array<string>;
  dataTableKeyFields: Array<COLUMN_TYPE>;
  _timeLimit: number | null;
  options: BulkImportOptions;
  selectedDestination: SelectedDestination | null;
  shouldCloseOnRequest: boolean;
}

function _optionSchemaPreferenceKey(option: string, tagSchemaId: string | null): string {
  // Generate a string key for import options whose values are saved via per-schema user prefs
  return `bulk_import.${option}-${tagSchemaId}`;
}

export const DERIVED_COLUMNS_DATA_INITIAL_STATE: DerivedColumnsData = {
  derivedColumns: null,
  state: null,
};

export class SpreadsheetBulkImportStore extends Store {
  derivedColumnsData!: DerivedColumnsData;
  file!: File | null;
  fileData!: FileData | null;
  selectedSchemaIds!: SelectedSchemaIds;
  forInventoryFileCreate!: boolean;
  importSourceType!: ImportSourceType;
  importType!: IMPORT_TYPE | null;
  errorData!: ErrorData | null;
  linkFolderIds!: Array<string>;
  monomerMapping!: MonomerMapping | null;
  panes!: Array<Pane>;
  panesTimeTracking!: {[paneKey in PaneKey]?: number};
  panesTimeTrackingCurrentTimestamp!: number;
  paneKey!: PaneKey | null;
  rawText!: string;
  registry!: Registry | undefined;
  registryImportSettings!: RegistryImportSettingsProps;
  schemalessCreationEntityType!: SchemalessBulkImportSupportedOligoAwareTypes | null;
  spreadsheetColumnsMappingName!: string | null;
  tableColumnNames!: Array<string>;
  dataTableKeyFields!: Array<COLUMN_TYPE>;
  _timeLimit!: number | null;
  options!: BulkImportOptions;
  selectedDestination!: SelectedDestination | null;
  shouldCloseOnRequest!: boolean;

  selectedTagSchemaId!: string | null;
  selectedContainerSchemaId!: string | null;
  selectedPlateSchemaId!: string | null;
  selectedBoxSchemaId!: string | null;
  selectedLocationSchemaId!: string | null;
  selectedSampleGroupSchemaId!: string | null;
  selectedResultSchemaId!: string | null;
  selectedProcessTaskSchemaId!: string | null;
  selectedProcessOutputSchemaId!: string | null;
  selectedTrialSubjectSchemaId!: string | null;

  override resetState(): void {
    this.importType = null;
    this.panes = [];
    this.panesTimeTracking = {};
    this.panesTimeTrackingCurrentTimestamp = 0;
    this.paneKey = null;
    this.file = null;
    this.rawText = '';
    this.fileData = null;
    this.derivedColumnsData = DERIVED_COLUMNS_DATA_INITIAL_STATE;
    this.monomerMapping = null;
    this.errorData = null;
    this._timeLimit = null;

    // Import settings
    this._resetSelectedSchemaIds();
    // Alternatively to selectedSchemaIds, the schemalessCreationEntityType can be specified directly
    this.schemalessCreationEntityType = null;
    // Selected destination `{folderId, registryId}` for importing into a project or registry.
    // Note: Importing REGISTRY_ENTITIES will set the registry destination through
    // `registryImportSettings` and `selectedDestination` will be `null`.
    this.selectedDestination = null;
    this.linkFolderIds = [];

    // `registryImportSettings` sets registry-specific settings when importing REGISTRY_ENTITIES.
    this._resetRegistryImportSettings();

    this.spreadsheetColumnsMappingName = null;
    this.tableColumnNames = [];
    this.dataTableKeyFields = [];

    // Initialize each option to false
    this.options = _.mapObject(_.invert(OPTIONS), () => false) as BulkImportOptions;

    // When false, `SpreadsheetBulkImportActions.closeModal()` is a noop. Used by `wrapDaoImportWithDataMods`
    // to keep the spreadsheet modal open until the data mods modal is submitted.
    this.shouldCloseOnRequest = true;
  }

  getState(): SpreadsheetBulkImportState {
    return {
      importSourceType: this._getImportSourceType(),
      importType: this.importType,
      derivedColumnsData: this.derivedColumnsData,
      errorData: this.errorData,
      file: this.file,
      fileData: this.fileData,
      linkFolderIds: this.linkFolderIds,
      monomerMapping: this.monomerMapping,
      options: this.options,
      panes: this.panes,
      panesTimeTracking: this.panesTimeTracking,
      panesTimeTrackingCurrentTimestamp: this.panesTimeTrackingCurrentTimestamp,
      paneKey: this.paneKey,
      rawText: this.rawText,
      registryImportSettings: this.registryImportSettings,
      schemalessCreationEntityType: this.schemalessCreationEntityType,
      spreadsheetColumnsMappingName: this.spreadsheetColumnsMappingName,
      selectedDestination: this.selectedDestination,
      selectedSchemaIds: {
        tagSchemaId: this.selectedTagSchemaId,
        containerSchemaId: this.selectedContainerSchemaId,
        plateSchemaId: this.selectedPlateSchemaId,
        boxSchemaId: this.selectedBoxSchemaId,
        locationSchemaId: this.selectedLocationSchemaId,
        sampleGroupSchemaId: this.selectedSampleGroupSchemaId,
        resultSchemaId: this.selectedResultSchemaId,
        processTaskSchemaId: this.selectedProcessTaskSchemaId,
        processOutputSchemaId: this.selectedProcessOutputSchemaId,
        trialSubjectSchemaId: this.selectedTrialSubjectSchemaId,
      },
      tableColumnNames: this.tableColumnNames,
      dataTableKeyFields: this.dataTableKeyFields,
      _timeLimit: this._timeLimit,
      shouldCloseOnRequest: this.shouldCloseOnRequest,
    };
  }

  _resetSelectedSchemaIds(): void {
    this.selectedTagSchemaId = null;
    this.selectedContainerSchemaId = null;
    this.selectedPlateSchemaId = null;
    this.selectedBoxSchemaId = null;
    this.selectedLocationSchemaId = null;
    this.selectedSampleGroupSchemaId = null;
    this.selectedResultSchemaId = null;
    this.selectedProcessTaskSchemaId = null;
    this.selectedProcessOutputSchemaId = null;
    this.selectedTrialSubjectSchemaId = null;
  }

  _resetRegistryImportSettings(): void {
    this.registryImportSettings = {
      registry: null,
      inOrgCollection: false,
      inUserCollection: false,
      labelingStrategy: null,
      labelingStrategyOptions: [],
      trialSubjectLabelingStrategy: null,
      trialSubjectLabelingStrategyOptions: [],
      projectId: null,
    };
  }

  _resetOptionsFromPreferences(tagSchemaId: string | null): void {
    // Set every option to its default value for the new schema, if it exists
    const optionsFromPreferences: {[key in OPTIONS]?: boolean} = {};
    // eslint-disable-next-line no-restricted-syntax
    _.each(OPTIONS_SAVED_AS_PREFERENCE, (option) => {
      optionsFromPreferences[option] =
        preferences.get(_optionSchemaPreferenceKey(option, tagSchemaId)) || false;
    });
    this.options = {...this.options, ...optionsFromPreferences};
  }

  _setPreferenceForOptions(options: {[key in OPTIONS]?: boolean}): void {
    Object.keys(options).forEach((optionName: OPTIONS) => {
      const optionValue = options[optionName];
      if (OPTIONS_SAVED_AS_PREFERENCE.includes(optionName) && this.selectedTagSchemaId) {
        preferences.set(_optionSchemaPreferenceKey(optionName, this.selectedTagSchemaId), optionValue);
      }
    });
  }

  _getImportSourceType(): ImportSourceType {
    return preferences.get(SpreadsheetBulkImportPreferences.SOURCE_TYPE) || ImportSourceType.SPREADSHEET;
  }

  _removeSpreadsheet(): void {
    assert(this.paneKey === PaneKey.UPLOAD_SPREADSHEET, 'Can only remove spreadsheet from the upload pane.');
    this.file = null;
    this.fileData = null;
    this.derivedColumnsData = DERIVED_COLUMNS_DATA_INITIAL_STATE;
  }

  _updatePaneTimeTrackingAndResetTimer(): void {
    if (!this.paneKey) {
      return;
    }
    const time: number = Date.now() - this.panesTimeTrackingCurrentTimestamp;
    const currentPaneStoredTime = this.panesTimeTracking[this.paneKey];
    this.panesTimeTracking[this.paneKey] = (currentPaneStoredTime ?? 0) + time;
    this.panesTimeTrackingCurrentTimestamp = Date.now();
  }

  finalizeTimeTrackingAndReturnSecondsSpentOnPanes(): {[paneKey in PaneKey]?: number} {
    this._updatePaneTimeTrackingAndResetTimer();
    const timeTrackingInSeconds = Object.fromEntries(
      Object.entries(this.panesTimeTracking).map(([paneKey, time]) => [
        paneKey.toLowerCase(),
        toSeconds(time),
      ])
    );
    return timeTrackingInSeconds;
  }

  /* Generate schema analytics metadata */
  _getSchemaIdAnalyticsMetadata(): {
    tagSchemaId?: string | null;
    trialSampleSchemaId?: string | null;
    trialSubjectSchemaId?: string | null;
  } {
    const schemaAnalyticsMetadata =
      this.importType === IMPORT_TYPE.TRIAL_SAMPLES
        ? {
            trialSampleSchemaId: this.selectedTagSchemaId,
            trialSubjectSchemaId: this.selectedTrialSubjectSchemaId,
          }
        : {tagSchemaId: this.selectedTagSchemaId};

    return schemaAnalyticsMetadata;
  }

  _setPrevPane(): void {
    // Update the time tracking for the current pane before moving to the previous pane
    this._updatePaneTimeTrackingAndResetTimer();

    const newIdx = this.panes.findIndex(({paneKey}) => paneKey === this.paneKey) - 1;
    const newPaneKey = this.panes[newIdx].paneKey;
    assert(newPaneKey != null, 'Invalid pane!');
    this.paneKey = newPaneKey;

    const schemaAnalyticsMetadata = this._getSchemaIdAnalyticsMetadata();

    analytics.track('bulk_spreadsheet', 'set_prev', {
      importType: this.importType,
      pane: this.paneKey,
      schemalessCreationEntityType: this.schemalessCreationEntityType,
      schemaEntityType:
        this.selectedTagSchemaId && getTagSchema(this.getState().selectedSchemaIds)
          ? getTagSchemaOligoAwareFolderItemType(getTagSchema(this.getState().selectedSchemaIds)!)
          : null,
      ...schemaAnalyticsMetadata,
    });
  }

  _setNextPane(): void {
    // Update the time tracking for the current pane before moving to the next pane
    this._updatePaneTimeTrackingAndResetTimer();

    const newIdx = this.panes.findIndex(({paneKey}) => paneKey === this.paneKey) + 1;
    const newPaneKey = this.panes[newIdx].paneKey;
    assert(newPaneKey != null, 'Invalid pane!');
    this.paneKey = newPaneKey;

    const schemaAnalyticsMetadata = this._getSchemaIdAnalyticsMetadata();

    analytics.track('bulk_spreadsheet', 'set_next', {
      importType: this.importType,
      pane: this.paneKey,
      schemalessCreationEntityType: this.schemalessCreationEntityType,
      schemaEntityType:
        this.selectedTagSchemaId && getTagSchema(this.getState().selectedSchemaIds)
          ? getTagSchemaOligoAwareFolderItemType(getTagSchema(this.getState().selectedSchemaIds)!)
          : null,
      ...schemaAnalyticsMetadata,
    });
  }

  _setPaneByKey(paneKeyToSet: PaneKey): void {
    // Update the time tracking for the current pane before moving to another pane
    this._updatePaneTimeTrackingAndResetTimer();

    const schemaAnalyticsMetadata = this._getSchemaIdAnalyticsMetadata();

    assert(this.panes.findIndex(({paneKey}) => paneKey === paneKeyToSet) !== -1, 'Invalid pane!');
    this.paneKey = paneKeyToSet;
    analytics.track('bulk_spreadsheet', 'set_pane', {
      importType: this.importType,
      pane: this.paneKey,
      schemalessCreationEntityType: this.schemalessCreationEntityType,
      schemaEntityType:
        this.selectedTagSchemaId && getTagSchema(this.getState().selectedSchemaIds)
          ? getTagSchemaOligoAwareFolderItemType(getTagSchema(this.getState().selectedSchemaIds)!)
          : null,
      ...schemaAnalyticsMetadata,
    });
  }

  _settingsFromRegistry(registry: Optional<Registry>): {
    registry?: Registry;
    inOrgCollection?: boolean;
    inUserCollection?: boolean;
    labelingStrategy?: null;
    trialSubjectLabelingStrategy?: null;
  } {
    if (!registry) {
      return {};
    }

    const settings: {
      registry?: Registry;
      inOrgCollection?: boolean;
      inUserCollection?: boolean;
      labelingStrategy?: null;
      trialSubjectLabelingStrategy?: null;
    } = {registry};

    if (registry.disablePersonalCollections) {
      settings.inOrgCollection = true;
      settings.inUserCollection = false;
    }

    settings.labelingStrategy = null;
    settings.trialSubjectLabelingStrategy = null;

    return settings;
  }

  /**
   * Set any defined settings. If any selectedSchemaIds change, recompute guessed column types.
   */
  _setDefinedSettings(settings: Omit<ImportSettings, 'registryImportSettings'>): void {
    const oldSelectedSchemaIds = this.getState().selectedSchemaIds;
    Object.assign(this, selectDefined(settings));
    // If any of the selected schemas change, recompute the guessed column types since the schema
    // field column type options are no longer valid.
    if (!isEqual(oldSelectedSchemaIds, this.getState().selectedSchemaIds)) {
      this._recomputeGuessedColumnTypes();
    }
  }

  _updateImportSettings(settings: ImportSettings): void {
    const {
      linkFolderIds,
      schemalessCreationEntityType,
      selectedDestination,
      selectedTagSchemaId,
      selectedContainerSchemaId,
      selectedPlateSchemaId,
      selectedBoxSchemaId,
      selectedLocationSchemaId,
      selectedSampleGroupSchemaId,
      selectedResultSchemaId,
      selectedProcessTaskSchemaId,
      selectedProcessOutputSchemaId,
      selectedTrialSubjectSchemaId,
      registryImportSettings,
    } = settings;

    this._setDefinedSettings({
      linkFolderIds,
      schemalessCreationEntityType,
      selectedDestination,
      selectedContainerSchemaId,
      selectedPlateSchemaId,
      selectedBoxSchemaId,
      selectedLocationSchemaId,
      selectedSampleGroupSchemaId,
      selectedResultSchemaId,
      selectedProcessTaskSchemaId,
      selectedProcessOutputSchemaId,
    });

    // Handle tag schema separately, since we recompute the batch schema and other settings upon change.
    if (selectedTagSchemaId !== undefined) {
      this._setDefinedSettings({
        selectedTagSchemaId,
      });
      this.registryImportSettings = {
        ...this.registryImportSettings,
        ...this._getRecomputedLabelingStrategySettings(selectedTagSchemaId),
      };
      this._resetOptionsFromPreferences(selectedTagSchemaId);
    }

    // Handle trial subject schema separately, since we recompute other settings upon change.
    if (selectedTrialSubjectSchemaId !== undefined) {
      this._setDefinedSettings({
        selectedTrialSubjectSchemaId,
      });

      const {
        labelingStrategy: trialSubjectLabelingStrategy,
        labelingStrategyOptions: trialSubjectLabelingStrategyOptions,
      } = this._getRecomputedLabelingStrategySettings(selectedTrialSubjectSchemaId);

      this.registryImportSettings = {
        ...this.registryImportSettings,
        trialSubjectLabelingStrategy,
        trialSubjectLabelingStrategyOptions,
      };
    }

    if (registryImportSettings) {
      const registry = registryImportSettings.registry;
      // If the registry changes, unset all the schemas since they're no longer relevant.
      // TODO(BNCH-74699): this.registry is never set, is this safe to remove?
      if (!isEqual(registry, this.registry)) {
        this._resetSelectedSchemaIds();
      }

      const updatedRegistryImportSettings = Object.assign(
        pickDefined(
          registryImportSettings,
          'inOrgCollection',
          'inUserCollection',
          'labelingStrategy',
          'trialSubjectLabelingStrategy',
          'projectId'
        ),
        this._settingsFromRegistry(registry)
      );

      this.registryImportSettings = {
        ...this.registryImportSettings,
        ...updatedRegistryImportSettings,
      };
    }
  }

  _updateColumns(columns: Array<SpreadsheetColumn>): void {
    this.fileData = reactUpdate(this.fileData, {
      columns: {$set: columns},
    });
  }

  _updateDerivedColumns(derivedColumns: Array<DerivedColumn>): void {
    this.derivedColumnsData.derivedColumns = derivedColumns;
  }

  _getRecomputedLabelingStrategySettings(tagSchemaId: string | null): {
    labelingStrategy: LabelingStrategy;
    labelingStrategyOptions: Array<LabelingStrategy>;
  } {
    const tagSchema = tagSchemaId ? getTranslatedTagSchemaFromCache(tagSchemaId) : null;
    if (!tagSchemaId || !tagSchema) {
      return {
        labelingStrategy: RegistryLabelingStrategy.IDS_FROM_NAMES,
        labelingStrategyOptions: [RegistryLabelingStrategy.IDS_FROM_NAMES],
      };
    }
    // Allow falling back to DEFAULT_LABELING_STRATEGY, which may not be enabled on all deploys.
    const labelingStrategyOptions = LabelingStrategiesUtil.filterLabelingStrategies(
      SPREADSHEET_LABELING_STRATEGIES,
      tagSchema,
      this.registryImportSettings.registry
    );
    return {
      labelingStrategy: LabelingStrategiesUtil.getDefaultLabelingStrategy(labelingStrategyOptions, [
        tagSchemaId,
      ]),
      labelingStrategyOptions,
    };
  }

  _getValidColumnTypes(): Array<COLUMN_TYPE> {
    // Use the first pane which has a getValidColumnTypes function, which could currently be either
    // MAP_COLUMN_NAMES or MAP_OUTPUT_FILE_COLUMNS
    const findPane = this.panes.find(
      (pane) =>
        pane.configuration &&
        // eslint-disable-next-line benchlint/no-in-operator
        'getValidColumnTypes' in pane.configuration &&
        pane.configuration.getValidColumnTypes instanceof Function
    )!;
    if (!findPane) {
      return [COLUMN_TYPE.IGNORE];
    }
    const {configuration} = findPane;
    return (configuration as {getValidColumnTypes: GetValidColumnTypesFunction})
      .getValidColumnTypes({
        selectedSchemaIds: this.getState().selectedSchemaIds,
        registryImportSettings: this.registryImportSettings,
        options: this.options,
        dataTableKeyFields: this.dataTableKeyFields,
        schemalessCreationEntityType: this.schemalessCreationEntityType,
      })
      .filter(
        (columnType) =>
          isBatchesEnabled() || ![COLUMN_TYPE.BATCH_NAME, COLUMN_TYPE.INPUT_BATCH_NAME].includes(columnType)
      );
  }

  /** Return a function that is able to fetch the usable schema fields for a specific column index. If no
   * function applies, return undefined. */
  private getValidSchemaFieldIdsForColumnFunction(): ((columnIndex: number) => Array<string>) | undefined {
    const mapColumnsPane = this.panes.find(
      (pane): pane is MapColumnsPane =>
        pane.configuration != null && pane.configuration.hasOwnProperty('getValidSchemaFieldIds')
    );
    return mapColumnsPane?.configuration.getValidSchemaFieldIds;
  }

  _completeFileDataUpload({
    columnNames,
    dataTypes,
    url,
  }: {
    columnNames: Array<string>;
    dataTypes?: Array<string>;
    url: string;
  }): void {
    const validColumnTypes = this._getValidColumnTypes();
    const {tableColumnNames, dataTableKeyFields} = this.getState();
    this.fileData = {
      url,
      columns: amendWithGuessedType(
        this.importType!,
        validColumnTypes,
        columnNames,
        this.getState().selectedSchemaIds,
        {tableColumnNames, dataTableKeyFields},
        dataTypes,
        this.getValidSchemaFieldIdsForColumnFunction()
      ),
      state: TaskState.COMPLETED,
    };
  }

  _completeFindDerivedColumns({
    derivedColumnsResponse,
  }: {
    derivedColumnsResponse: FindDerivedColumnsResponse;
  }): void {
    const derivedColumns = Object.keys(derivedColumnsResponse).map((columnTransform: ColumnTransform) => ({
      columnTransform,
      allowedColumnTypes: derivedColumnsResponse[columnTransform].columnTypes,
      name: columnTransform,
      numRowsWithDerivedValues: derivedColumnsResponse[columnTransform].numRowsWithDerivedValues,
      exampleValues: derivedColumnsResponse[columnTransform].exampleValues,
      compatibleSchemaFieldIds: derivedColumnsResponse[columnTransform].compatibleSchemaFieldIds,
    }));
    this.derivedColumnsData = {
      ...this.derivedColumnsData,
      derivedColumns: getDerivedColumnsWithGuessedTypes(derivedColumns, this.getState().selectedSchemaIds),
      state: TaskState.COMPLETED,
    };
  }

  /**
   * Compute the column type guesses if the spreadsheet is loaded. If it's not loaded, they'll be
   * computed later in the SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED handler.
   */
  _recomputeGuessedColumnTypes(): void {
    if (this.fileData && this.fileData.columns) {
      const validColumnTypes = this._getValidColumnTypes();
      const columnNames = this.fileData.columns.map(({name}) => name);
      const {tableColumnNames, dataTableKeyFields} = this.getState();
      this._updateColumns(
        amendWithGuessedType(
          this.importType!,
          validColumnTypes,
          columnNames,
          this.getState().selectedSchemaIds,
          {tableColumnNames, dataTableKeyFields},
          undefined,
          this.getValidSchemaFieldIdsForColumnFunction()
        )
      );
    }
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.SPREADSHEET_BULK_IMPORT_BEGIN_IMPORT_FLOW:
        assert(
          Object.values(IMPORT_TYPE).includes(action.importType),
          `Invalid importType specified: ${action.importType}`
        );
        this.panes = action.panes;
        this.paneKey = action.initialPaneKey || this.panes[0].paneKey;
        this.importType = action.importType;
        if (action.initialRegistry) {
          const updatedRegistryImportSettings = {
            inOrgCollection: false,
            inUserCollection: true,
            labelingStrategy: null,
            labelingStrategyOptions: [],
            trialSubjectLabelingStrategy: null,
            trialSubjectLabelingStrategyOptions: [],
            ...this._settingsFromRegistry(action.initialRegistry),
          };
          this.registryImportSettings = {...this.registryImportSettings, ...updatedRegistryImportSettings};
        }
        if (action.initialFolder) {
          this.selectedDestination = action.initialFolder;
        }
        if (action.initialContainerSchemaId) {
          this.selectedContainerSchemaId = action.initialContainerSchemaId;
        }
        if (action.initialTagSchemaId) {
          this.selectedTagSchemaId = action.initialTagSchemaId;
        }
        if (action.initialSampleGroupSchemaId) {
          this.selectedSampleGroupSchemaId = action.initialSampleGroupSchemaId;
        }
        if (action.initialResultSchemaId) {
          this.selectedResultSchemaId = action.initialResultSchemaId;
        }
        if (action.initialPlateSchemaId) {
          this.selectedPlateSchemaId = action.initialPlateSchemaId;
        }
        if (action.initialBoxSchemaId) {
          this.selectedBoxSchemaId = action.initialBoxSchemaId;
        }
        if (action.initialProcessTaskSchemaId) {
          this.selectedProcessTaskSchemaId = action.initialProcessTaskSchemaId;
        }
        if (action.initialProcessOutputSchemaId) {
          this.selectedProcessOutputSchemaId = action.initialProcessOutputSchemaId;
        }
        if (action.initialTrialSubjectSchemaId) {
          this.selectedTrialSubjectSchemaId = action.initialTrialSubjectSchemaId;
        }
        if (action.initialTableColumnNames) {
          this.tableColumnNames = action.initialTableColumnNames;
        }
        if (action.initialDataTableKeyFields) {
          this.dataTableKeyFields = action.initialDataTableKeyFields;
        }
        if (action.initialSchemalessCreationEntityType) {
          this.schemalessCreationEntityType = action.initialSchemalessCreationEntityType;
        }
        if (action.initialRawText) {
          this.rawText = action.initialRawText;
        }
        analytics.track('bulk_spreadsheet', 'begin', {
          importType: this.importType,
          schemalessCreationEntityType: this.schemalessCreationEntityType,
        });
        this.panesTimeTracking = {};
        this.panesTimeTrackingCurrentTimestamp = Date.now();
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_OPTIONS: {
        this.options = {...this.options, ...action.options};
        this._setPreferenceForOptions(action.options);
        this.emitChange();
        break;
      }

      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_IMPORT_SETTING:
        this._updateImportSettings(action as ImportSettings);
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_TIME_LIMITS:
        this._timeLimit = action._timeLimit;
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_SOURCE_TYPE_CHANGED:
        preferences.set(SpreadsheetBulkImportPreferences.SOURCE_TYPE, action.value);
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_PREV_PANE:
        this._setPrevPane();
        if (
          this.paneKey === PaneKey.UPLOAD_SPREADSHEET &&
          this._getImportSourceType() === ImportSourceType.RAW_TEXT
        ) {
          this._removeSpreadsheet();
        }
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_NEXT_PANE:
        this._setNextPane();
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_PANE_WITH_KEY:
        this._setPaneByKey(action.paneKey);
        this.emitChange();
        break;

      // Upload spreadsheet
      case EventConstants.SPREADSHEET_BULK_IMPORT_RAW_TEXT_UPDATED:
        this.rawText = action.rawText;
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE:
        this.file = action.file;
        this.fileData = {
          url: '',
          columns: [],
          state: TaskState.STARTED,
        };
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED:
        if (action.file !== this.file) {
          break;
        }
        this.fileData = {
          url: '',
          columns: [],
          errorMessage: action.errorMessage,
          state: TaskState.FAILED,
        };
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED: {
        if (action.file !== this.file) {
          break;
        }
        const {columnNames, dataTypes, url} = action;
        this._completeFileDataUpload({columnNames, dataTypes, url});
        this.emitChange();
        break;
      }
      // Spreadsheet Pane actions
      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_SPREADSHEET_COLUMNS_MAPPING_NAME:
        this.spreadsheetColumnsMappingName = action.spreadsheetColumnsMappingName;
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_COLUMNS:
        this._updateColumns(action.columns);
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_REMOVE_SPREADSHEET:
        this._removeSpreadsheet();
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE:
        this.errorData = {
          ...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
          state: TaskState.STARTED,
        };
        this.emitChange();
        break;

      // Derived column actions
      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_DERIVED_COLUMNS:
        this._updateDerivedColumns(action.derivedColumns);
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS:
        this.derivedColumnsData.state = TaskState.STARTED;
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_COMPLETED: {
        const {derivedColumnsResponse} = action;
        this._completeFindDerivedColumns({derivedColumnsResponse});
        this.emitChange();
        break;
      }
      case EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_FAILED:
        this.derivedColumnsData = {
          derivedColumns: null,
          state: TaskState.FAILED,
        };
        this.emitChange();
        break;
      // Error Pane actions
      case EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE_FAILED:
        if (!this.fileData || this.fileData.url !== action.url) {
          break;
        }
        this.errorData = {
          ...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
          state: TaskState.FAILED,
        };
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE_COMPLETED:
        if (!this.fileData || this.fileData.url !== action.url) {
          break;
        }
        this.errorData = {
          itemNameErrors: action.itemNameErrors || [],
          registryErrors: action.registryErrors || [],
          registryWarnings: action.registryWarnings || [],
          otherErrors: action.otherErrors || [],
          otherWarnings: action.otherWarnings || [],
          studyWarnings: action.studyWarnings || [],
          tagErrors: action.tagErrors || [],
          state: TaskState.COMPLETED,
        };

        analytics.track('bulk_spreadsheet', 'validation_completed', {
          importType: this.importType,
          schemalessCreationEntityType: this.schemalessCreationEntityType,
          schemaEntityType:
            this.selectedTagSchemaId && getTagSchema(this.getState().selectedSchemaIds)
              ? getTagSchemaOligoAwareFolderItemType(getTagSchema(this.getState().selectedSchemaIds)!)
              : null,
          filenameErrorCount: this.errorData.itemNameErrors.length,
          otherErrorCount: this.errorData.otherErrors.length,
          otherWarningCount: this.errorData.otherWarnings.length,
          registryErrorCount: this.errorData.registryErrors.length,
          studyWarningCount: this.errorData.studyWarnings.length,
          tagErrorCount: this.errorData.tagErrors.length,
          ...this._getSchemaIdAnalyticsMetadata(),
        });
        this.emitChange();
        break;

      // On close
      case EventConstants.SPREADSHEET_BULK_IMPORT_RESET:
        this.resetState();
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_SET_SHOULD_CLOSE_ON_REQUEST:
        // Used by `wrapDaoImportWithDataMods` to keep the spreadsheet modal open until the data mods modal
        // is submitted.
        this.shouldCloseOnRequest = action.shouldCloseOnRequest;
        this.emitChange();
        break;

      case EventConstants.SPREADSHEET_BULK_IMPORT_UPDATE_MONOMER_MAPPING:
        this.monomerMapping = action.monomerMapping;
        this.emitChange();
        break;

      default:
        break;
    }
    return true;
  }
}

const bulkImportSpreadsheetStore = new SpreadsheetBulkImportStore(AppDispatcher);

export default bulkImportSpreadsheetStore;
